import { Injectable, StaticProvider, TemplateRef, Type } from "@angular/core";

import {
  ComponentOverlayRef,
  OverlayService,
  PositionOption,
  WindowPositionStrategy
} from "../overlays";

import { ModalContainerComponent } from "@lantern/app/common/utils/modals/modal-container.component";
import { ModalTemplateContext } from "@lantern/app/common/utils/modals/modal-template.directive";
import { ModalComponent } from "@lantern/app/common/utils/modals/modal.component";

import { ModalRef } from "./modal-ref";
import { MODAL_COMPONENT_TYPE, MODAL_TEMPLATE_OPTIONS } from "./modal-tokens";

export type ModalProvidersBuilder<Response> = (
  modalRef: ModalRef<Response>,
  overlayRef: ComponentOverlayRef<ModalContainerComponent<Response>>
) => StaticProvider[];

@Injectable()
export class ModalService {

  constructor(private overlayService: OverlayService) {}

  openComponent<M extends ModalComponent<Response>, Response>(
    modalComponentType: Type<M>,
    providers: ModalProvidersBuilder<Response> | StaticProvider[] = [],
    dismissHandler?: () => Response | Error,
    position: PositionOption = "center",
    windowPadding: number = 20
  ): ModalRef<Response> {
    return this.open(
      (modalRef, overlayRef) => {
        if (typeof providers === "function") {
          providers = providers(modalRef, overlayRef);
        }
        return [
          ...providers,
          {
            provide: MODAL_COMPONENT_TYPE,
            useValue: modalComponentType
          }
        ];
      },
      dismissHandler,
      position,
      windowPadding
    );
  }

  openTemplate<Response>(
    templateRef: TemplateRef<ModalTemplateContext<Response>>,
    dismissHandler?: () => Response | Error,
    position: PositionOption = "center",
    windowPadding: number = 20
  ): ModalRef<Response> {
    return this.open(
      (modalRef, overlayRef) => {
        interface ContextBindings {
          close: (response: Response) => void;
          dismiss: (err: any) => void;
        }

        let bindings: ContextBindings = {
          close: (response: Response) => modalRef.close(response),
          dismiss: (err: any) => modalRef.dismiss(err)
        };

        return [
          {
            provide: MODAL_TEMPLATE_OPTIONS,
            useValue: {
              templateRef,
              context: Object.assign({}, bindings, {
                $implicit: bindings
              })
            }
          }
        ];
      },
      dismissHandler,
      position,
      windowPadding
    );
  }

  private open<Response>(
    providers: ModalProvidersBuilder<Response>,
    dismissHandler?: () => Response | Error,
    position: PositionOption = "center",
    windowPadding: number = 20
  ): ModalRef<Response> {
    let modalRef: ModalRef<Response> | undefined;

    let positionStrategy: WindowPositionStrategy = new WindowPositionStrategy(
      position,
      windowPadding
    );
    this.overlayService.createComponent<ModalContainerComponent<Response>>(
      ModalContainerComponent,
      positionStrategy,
      undefined,
      (overlayRef) => {
        modalRef = new ModalRef<Response>(overlayRef, dismissHandler);
        return [
          ...providers(modalRef, overlayRef),
          {
            provide: ModalRef,
            useValue: modalRef
          }
        ];
      }
    );

    return modalRef!;
  }
}
